"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import useGetCMS from "@/hooks/use-get-cms";
import { blocksToString, limitTextBlock } from "@/lib/blocks";
import {
  generateFacebookShareLink,
  generateShareInternalLink,
  generateTwitterShareLink,
  generateWhatsAppShareLink,
} from "@/lib/social-media";
import { Link } from "@/src/i18n/navigation";
import { BlocksContent, BlocksRenderer } from "@strapi/blocks-react-renderer";
import dayjs from "dayjs";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";

export interface NewsEventDetailProps {
  title: string;
  subtitle: string;
  created_at: string;
  custom_date: string;
  category: string;
  content: BlocksContent;
  slug: string;
  thumbnail: string;
}

export default function NewsEventDetail(props: NewsEventDetailProps) {
  const category = props.category;
  const lang = useLocale()

  const [shareText, setshareText] = useState("");

  const { data } = useGetCMS({
    collection: "new-and-events",
    key: `new-and-events-${props.slug}-${lang}`,
    config: {
      pLevel: 2,
      locale: lang,
      filters: {
        category: {
          $eq: category,
        },
      },
    },
  });

  useEffect(() => {
    const url = new URL(window.location.pathname, window.location.origin);
    setshareText(`${props.title} ${url.toString()}`);
  }, [props.title]);

  const relatedPosts = data?.data ?? [];

  return (
    <>
      <div className="bg-coffebrown pb-4 xl:py-28 *:whitespace-normal pt-20 xl:pt-0">
        <div className="w-full xl:max-w-screen-xl mx-auto px-9 xl:px-0 xl:pt-36">
          <Breadcrumb className="mb-7 hidden xl:block ">
            <BreadcrumbList>
              <BreadcrumbItem className="text-white text-2xl">
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-2xl text-white">
                {">"}
              </BreadcrumbSeparator>
              <BreadcrumbItem className="text-white text-2xl">
                <BreadcrumbLink href="/news-event">News & Event</BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbSeparator className="text-2xl text-white">
                {">"}
              </BreadcrumbSeparator>
              <BreadcrumbItem className="text-white text-2xl">
                <BreadcrumbLink>News</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <h1
            className="text-white xl:text-gold text-2xl xl:text-6xl  text-left font-pluto"
            dangerouslySetInnerHTML={{
              __html: `${props.title}${props.subtitle ? ":" : ""}`,
            }}
          ></h1>

          {props.subtitle && (
            <p className="text-white xl:text-gold text-2xl xl:text-6xl  text-left font-pluto">
              {props.subtitle}
            </p>
          )}

          <div className="flex flex-row xl:flex-col items-center xl:items-start justify-between mt-6 mb-2 xl:mb-10">
            <p className=" text-white xl:text-2xl mt-4">
              {dayjs(props.custom_date).format("MMMM DD, YYYY")}
            </p>
            <div className="flex items-center mt-4">
              <span className="text-white xl:text-2xl">Share:</span>
              <Link target="_blank" href={generateWhatsAppShareLink(shareText)}>
                <Image
                  src="/WhatsApp.png"
                  alt="whatsapp"
                  width={20}
                  height={20}
                  className="ml-2 size-7 xl:size-10"
                />
              </Link>
              <Link target="_blank" href={generateFacebookShareLink(shareText)}>
                <Image
                  src="/facebook.png"
                  alt="facebook"
                  width={20}
                  height={20}
                  className="ml-2 size-7 xl:size-10"
                />
              </Link>
              <Link target="_blank" href={generateTwitterShareLink(shareText)}>
                <Image
                  src="/twitter.png"
                  alt="twitter"
                  width={20}
                  height={20}
                  className="ml-2 size-7 xl:size-10"
                />
              </Link>
              <Image
                src="/share-link.png"
                alt="share-link"
                width={20}
                height={20}
                className="ml-2 cursor-pointer size-7 xl:size-10"
                onClick={() => {
                  generateShareInternalLink(shareText, window.location.href);
                }}
              />
            </div>
          </div>

          <div className="">
            <img
              src={props.thumbnail}
              alt=""
              className="rounded-[9px] xl:rounded-[2.5rem] object-cover w-full h-[242px] xl:h-[30rem]"
            />

            <div className="text-white mt-7 xl:text-2xl revert-all">
              <BlocksRenderer content={props.content} />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-coffebrown px-9 pb-7 pt-12">
        <div className="w-full xl:max-w-screen-xl mx-auto xl:border-t xl:border-t-white xl:pt-14">
          <p className="text-white font-medium text-center xl:text-left xl:text-5xl">
            Related Posts
          </p>

          <div className="flex gap-y-7 gap-x-2 xl:gap-x-14 mt-3.5 xl:mb-24">
            {relatedPosts.slice(0, 2).map((post) => (
              <Link
              locale={lang}
                key={post.id}
                className="flex-1"
                href={`/news-events/${post.slug}`}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_BASE_STATIC_URL}${post.images?.[0]?.url}`}
                  className="rounded-[9px] xl:rounded-[30px] w-full  object-cover h-[137px] xl:h-[321px]"
                />
                <p className="font-pluto font-medium text-[12px] xl:text-3xl text-white xl:text-gold whitespace-normal mt-3 xl:mt-7 mb-3.5 line-clamp-3">
                  {post.title}
                </p>
                <div className="block xl:hidden text-white text-[10px]">
                  {blocksToString(post.description.slice(0, 1))}
                </div>
                <p className="text-white hidden xl:block xl:text-2xl">
                  {dayjs(post.custom_date).format("MMMM DD, YYYY")}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
